/**
 * HTTP Service - Base service for API calls
 * Centralizes HTTP configuration and error handling
 */

import Vue from 'vue'
import * as Sentry from '@sentry/vue'

const { VUE_APP_API_HOST } = process.env

class HttpService {
  constructor() {
    this.baseURL = VUE_APP_API_HOST
    this._httpClient = null
  }

  /**
   * Initialize HTTP client (called from main.js after Vue setup)
   * @param {Object} httpClient - Vue HTTP client instance
   */
  init(httpClient) {
    this._httpClient = httpClient
    return this
  }

  /**
   * Get HTTP client instance
   * @returns {Object} HTTP client
   */
  get http() {
    if (!this._httpClient) {
      // Create a temporary Vue instance to access $http
      const tempVue = new Vue()
      if (tempVue.$http) {
        this._httpClient = tempVue.$http
      } else {
        throw new Error('HTTP client not available. Make sure vue-resource is loaded.')
      }
    }
    return this._httpClient
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Request configuration
   * @returns {Promise} API response
   */
  async get(endpoint, config = {}) {
    try {
      const response = await this.http.get(`${this.baseURL}${endpoint}`, config)
      return { success: true, data: response.body, response }
    } catch (error) {
      return this.handleError(error)
    }
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} config - Request configuration
   * @returns {Promise} API response
   */
  async post(endpoint, data = {}, config = {}) {
    try {
      const response = await this.http.post(
        `${this.baseURL}${endpoint}`,
        data,
        config
      )
      return { success: true, data: response.body, response }
    } catch (error) {
      return this.handleError(error)
    }
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} config - Request configuration
   * @returns {Promise} API response
   */
  async put(endpoint, data = {}, config = {}) {
    try {
      const response = await this.http.put(
        `${this.baseURL}${endpoint}`,
        data,
        config
      )
      return { success: true, data: response.body, response }
    } catch (error) {
      return this.handleError(error)
    }
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Request configuration
   * @returns {Promise} API response
   */
  async delete(endpoint, config = {}) {
    try {
      const response = await this.http.delete(
        `${this.baseURL}${endpoint}`,
        config
      )
      return { success: true, data: response.body, response }
    } catch (error) {
      return this.handleError(error)
    }
  }

  /**
   * Handle API errors consistently
   * @param {Object} error - Error object
   * @returns {Object} Formatted error response
   */
  handleError(error) {
    const errorResponse = {
      success: false,
      error: true,
      message: 'An error occurred',
      originalError: error
    }

    if (error && error.body) {
      errorResponse.data = error.body
      errorResponse.message = error.body.message || errorResponse.message
    }

    // Only report server/network failures: 4xx are expected app flow (bad password, validation, not found)
    const status = error && error.status
    if (!status || status >= 500) {
      Sentry.captureException(error)
    }

    return errorResponse
  }
}

// Export instance that can be initialized
const httpServiceInstance = new HttpService()
export default httpServiceInstance
